import React, { useEffect } from "react";
import {
  Document,
  Page,
  PDFViewer,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { Colors } from "../../../assets/Colors";
import { useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { objectDrawer, SortingPORT_Services } from "./OperationalOutputCommon";
import { useSelector } from "react-redux";

// ─── Date helpers ────────────────────────────────────────────────────────────

const fmtDate = (val) => {
  if (!val) return "-";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch { return String(val); }
};

const fmtDateTime = (val) => {
  if (!val) return "-";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return String(val); }
};

const fmtTime = (val) => {
  if (!val) return "-";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
  } catch { return String(val); }
};

// OUM lookup cache (will be populated on component mount)
let oumLookupCache = {};

const fmtOum = (oumId) => {
  if (!oumId || oumId === "") return "-";
  return oumLookupCache[oumId] || oumId;
};

// Jetty lookup cache
let jettyLookupCache = {};

const fmtJetty = (jettyId) => {
  if (!jettyId || jettyId === "") return "-";
  return jettyLookupCache[jettyId] || jettyId;
};

// ─── Column helpers ───────────────────────────────────────────────────────────

/**
 * Expand merged header rows into a flat list of column labels.
 * header_upper items with colSpan > 1 consume sub-labels from header[].
 * Items with text === "" are button-placeholder columns — skipped.
 */
function flattenHeaders(header_upper = [], header = []) {
  const labels = [];
  let subIdx = 0;
  for (const h of header_upper) {
    if (h.text === "") continue;
    const span = h.colSpan || 1;
    if (span > 1 && header.length > subIdx) {
      for (let i = 0; i < span; i++) {
        labels.push(header[subIdx + i] ? header[subIdx + i].text : h.text);
      }
      subIdx += span;
    } else {
      labels.push(h.text);
    }
  }
  return labels;
}

/** Body fields excluding button/numbering placeholders */
function getBodyFields(body = []) {
  return body.filter((f) => f.type !== "button" && f.type !== "numbering");
}

/** Render a cell value given field type, key, row data and row index */
function renderCell(type, key, row, rowIdx) {
  if (type === "numbering") return String(rowIdx + 1);
  
  // Computed fields (dynamic calculation)
  if (type === "computed") {
    // Total Hour untuk MANPOWER
    if (key === "__totalHour") {
      const start = row.date1 ? new Date(row.date1) : null;
      const end = row.date2 ? new Date(row.date2) : null;
      if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
        const hours = Math.round((end - start) / (1000 * 60 * 60));
        return hours >= 0 ? String(hours) : "0";
      }
      return "0";
    }
    
    // Total Hours untuk Vessel Service (date2 - date1 in hours)
    if (key === "__vesselTotalHours") {
      const start = row.date1 ? new Date(row.date1) : null;
      const end = row.date2 ? new Date(row.date2) : null;
      if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
        let hours = Math.round((end - start) / (1000 * 60 * 60));
        
        // Jika Activity (form5) adalah "Standby", maksimal 4 jam
        const activity = (row.form5 || "").toLowerCase();
        if (activity.includes("standby") || activity === "standby") {
          hours = Math.min(hours, 4);
        }
        
        return hours >= 0 ? String(hours) : "0";
      }
      return "0";
    }
    
    // Total untuk Liquid/Bulk Transfer (form4 - form3)
    if (key === "__liquidTotal") {
      const form3 = parseFloat(row.form3) || 0;
      const form4 = parseFloat(row.form4) || 0;
      return String(Math.max(0, form4 - form3));
    }
    
    // Total Days untuk AREA_RENTAL (date2 - date1 in days)
    if (key === "__totalDays" || key === "__vehicleTotalDays") {
      const start = row.date1 ? new Date(row.date1) : null;
      const end = row.date2 ? new Date(row.date2) : null;
      if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
        const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
        return days >= 0 ? String(days) : "0";
      }
      return "0";
    }
    
    // Total Dimension untuk AREA_RENTAL (L * W * H)
    if (key === "__totalDim") {
      const l = parseFloat(row.form4) || 0;
      const w = parseFloat(row.form5) || 0;
      const h = parseFloat(row.form6) || 0;
      return String(l * w * h);
    }
    
    // Total Meal untuk MEALS
    if (key === "__tMeal") {
      return row.form6 || "0";
    }
    
    // Total Time untuk WOODEN
    if (key === "__woodenTotal") {
      const start = row.date1 ? new Date(row.date1) : null;
      const end = row.date2 ? new Date(row.date2) : null;
      if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
        const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
        return days >= 0 ? String(days) : "0";
      }
      return "0";
    }
    
    // Total Time untuk Drill
    if (key === "__drillTotalTime") {
      const start = row.date1 ? new Date(row.date1) : null;
      const end = row.date2 ? new Date(row.date2) : null;
      if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
        const hours = Math.round((end - start) / (1000 * 60 * 60));
        return hours >= 0 ? String(hours) : "0";
      }
      return "0";
    }
    
    // Total Drill untuk Drill (form5 - form4)
    if (key === "__drillTotal") {
      const form4 = parseFloat(row.form4) || 0;
      const form5 = parseFloat(row.form5) || 0;
      return String(Math.max(0, form5 - form4));
    }
    
    // Total Fuel untuk Fuel (form5 - form4)
    if (key === "__fuelTotal") {
      const form4 = parseFloat(row.form4) || 0;
      const form5 = parseFloat(row.form5) || 0;
      return String(Math.max(0, form5 - form4));
    }
    
    // Total Area Rental untuk AREA_RENTAL (totalDim * Qty)
    if (key === "__tAreaRental") {
      const l = parseFloat(row.form4) || 0;
      const w = parseFloat(row.form5) || 0;
      const h = parseFloat(row.form6) || 0;
      const totalDim = l * w * h;
      const qty = parseFloat(row.form2) || 0;
      // Total Rental = Total Dim. × Qty
      return totalDim > 0 ? String(totalDim * qty) : "0";
    }
    
    // Total untuk RENTAL (based on UoM)
    if (key === "__rentalTotal") {
      const start = row.date1 ? new Date(row.date1) : null;
      const end = row.date2 ? new Date(row.date2) : null;
      const qty = parseFloat(row.form6) || 1;
      const uom = (row.form4 || "").toLowerCase();
      
      if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
        let total = 0;
        if (uom === "day") {
          // UoM = Day: calculate in days (inclusive, +1)
          const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
          total = days >= 0 ? Math.round(days * qty) : 0;
        } else if (uom === "hour") {
          // UoM = Hour: calculate in hours
          const hours = Math.round((end - start) / (1000 * 60 * 60));
          total = hours >= 0 ? Math.round(hours * qty) : 0;
        } else {
          // UoM = Other: just Qty
          total = qty;
        }
        return String(total);
      }
      // If no dates, just return Qty
      return String(qty);
    }
    
    return "-";
  }
  
  const val = key ? row[key] : null;
  if (val === undefined || val === null || val === "") return "-";
  if (type === "date") return fmtDate(val);
  if (type === "dateTime") return fmtDateTime(val);
  if (type === "time") return fmtTime(val);
  if (type === "oum") return fmtOum(val);
  if (type === "jetty") return fmtJetty(val);
  return String(val);
}
  
/** Table Cell Component dengan border yang benar (tanpa border di cell terakhirs ) */
const TableCell = ({ field, row, rowIndex, totalFields, width }) => {
  const isLastCell = rowIndex === totalFields - 1;
  const cellStyle = [
    s.tCell,
    { width },
    isLastCell && { borderRightWidth: 0 },
  ];
  
  return (
    <Text style={cellStyle}>
      {renderCell(field.type, field.key, row, rowIndex)}
    </Text>
  );
};

/**
 * Derive proportional column widths from header label text.
 * Wider text = wider column. Returns array of "XX.X%" strings.
 */
function deriveWidths(labels) {
  const weights = labels.map((lbl) => {
    const l = lbl.toLowerCase();
    if (l.includes("description") || l.includes("deskripsi") ||
        l.includes("material") || l.includes("nama")) return 24;
    if (l.includes("date") || l.includes("tanggal") || l.includes("time") ||
        l.includes("start") || l.includes("end") || l.includes("arrival") ||
        l.includes("departure") || l.includes("finish")) return 13;
    if (l.includes("remark") || l.includes("keterangan")) return 18;
    if (l.includes("number") || l.includes("vessel") || l.includes("sponsor") ||
        l.includes("company") || l.includes("manifest") || l.includes("transporter")) return 14;
    if (l.includes("uom") || l.includes("oum") || l.includes("qty") ||
        l.includes("total") || l.includes("weight") || l.includes("quantity")) return 10;
    if (l.includes("jetty") || l.includes("no ") || l.includes("no.")) return 10;
    return 11;
  });
  const sum = weights.reduce((a, b) => a + b, 0);
  return weights.map((w) => `${((w / sum) * 100).toFixed(1)}%`);
}

/** Map PORT_SERVICES sub-activity name to objectDrawer key */
function portServiceKey(name) {
  if (name === "Stevedoring") return "Port_Service_Common";
  if (name === "Liquid") return "Liquid";
  if (name === "Cargodoring") return "Cargodoring";
  const external = [
    "Receiving_Using_Forklift", "Receiving_Using_Crane",
    "Delivery_Using_Forklift", "Delivery_Using_Crane",
    "Delivery", "Receiving", "Stacking_atau_Restacking",
  ];
  if (external.includes(name)) return "Port_Service_External";
  return name; // Vessel_Service, Vessel_Berthing, Bulk_Transfer, Staging_for_loading, etc.
}

/** Map GENERAL_SERVICE sub-activity to objectDrawer key */
function generalServiceKey(sub) {
  let key = sub.DetailUnit;
  if (key === "Others") {
    key = sub.Name === "Material Supply" ? "Material_Supply" : sub.Name;
  }
  return key;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const C = {
  dark: "#000000",
  mid: "#000000",
  light: "#000000",
  accent: "#000000",
  bg: "#f7f8fa",
  border: "#000000",
  white: "#ffffff",
  rowAlt: "#f0f4f8",
};

// Diekspor agar cetakan JR V2 (CetakJrV2.jsx) memakai gaya yang SAMA PERSIS,
// bukan salinan yang lambat laun melenceng.
export const s = StyleSheet.create({
  page: {
    paddingTop: 20,
    paddingBottom: 45,
    paddingHorizontal: 28,
    fontFamily: "Helvetica",
    fontSize: 7,
    color: C.dark,
  },

  // ── Document header (3-column) ──────────────────────────────
  headerWrap: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: C.accent,
    marginBottom: 6,
    paddingBottom: 6,
    alignItems: "flex-start",
  },
  headerLeft: { flex: 2 },
  headerCenter: { flex: 2, alignItems: "center", justifyContent: "center" },
  headerRight: { flex: 2.5, alignItems: "flex-end" },

  companyName: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: C.accent,
    marginBottom: 1,
  },
  companyAddr: { fontSize: 5.5, color: C.light, lineHeight: 1.4 },

  titleBox: {
    backgroundColor: C.accent,
    paddingVertical: 5,
    paddingHorizontal: 18,
    borderRadius: 3,
    marginBottom: 4,
  },
  titleText: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: C.white,
    letterSpacing: 2,
  },

  docInfoTable: {
    borderWidth: 0.5,
    borderColor: C.accent,
    width: "100%",
  },
  docInfoRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: C.border,
  },
  docInfoRowLast: { flexDirection: "row" },
  docInfoLabel: {
    fontSize: 5.5,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRightWidth: 0.5,
    borderRightColor: C.border,
    width: "42%",
    color: C.light,
  },
  docInfoVal: {
    fontSize: 5.5,
    paddingHorizontal: 4,
    paddingVertical: 2,
    flex: 1,
    fontFamily: "Helvetica-Bold",
  },

  // ── Sub-header info box ──────────────────────────────────────
  subHeaderBox: {
    borderWidth: 0.5,
    borderColor: C.border,
    borderRadius: 2,
    marginBottom: 8,
    padding: 6,
    backgroundColor: C.bg,
  },
  subHeaderRow: { flexDirection: "row", marginBottom: 2 },
  subHeaderLabel: {
    fontSize: 6,
    width: 72,
    fontFamily: "Helvetica-Bold",
    color: C.mid,
  },
  subHeaderColon: { fontSize: 6, width: 8, textAlign: "center", color: C.mid },
  subHeaderVal: { fontSize: 6, flex: 1 },
  subHeaderGreeting: {
    fontSize: 6,
    marginTop: 4,
    marginBottom: 4,
    color: C.mid,
    fontStyle: "italic",
  },
  checklistWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 4,
  },
  checklistItem: {
    width: "33.3%",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 2,
  },
  checkBox: {
    fontSize: 6,
    width: 16,
    textAlign: "center",
    borderWidth: 0.5,
    borderColor: C.border,
    marginRight: 3,
    backgroundColor: C.white,
    paddingVertical: 1,
  },
  checkBoxChecked: {
    fontSize: 6,
    width: 16,
    textAlign: "center",
    borderWidth: 0.5,
    borderColor: C.accent,
    marginRight: 3,
    backgroundColor: C.accent,
    color: C.white,
    paddingVertical: 1,
  },
  checkLabel: { fontSize: 6, color: C.dark, flex: 1 },

  // ── Section title bar ─────────────────────────────────────────
  secTitle: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    backgroundColor: C.accent,
    color: C.white,
    paddingVertical: 3,
    paddingHorizontal: 5,
    marginTop: 8,
    marginBottom: 2,
  },
  subTitle: {
    fontSize: 6.5,
    fontFamily: "Helvetica-Bold",
    backgroundColor: C.mid,
    color: C.white,
    paddingVertical: 2,
    paddingHorizontal: 5,
    marginTop: 4,
    marginBottom: 1,
  },

  // ── Table ──────────────────────────────────────────────────────
  table: { marginBottom: 4 },
  tRowHead: {
    flexDirection: "row",
    backgroundColor: C.dark,
    minHeight: 14,
    borderWidth: 0.5,
    borderColor: C.border,
  },
  tRow: {
    flexDirection: "row",
    borderLeftWidth: 0.3,
    borderRightWidth: 0.3,
    borderTopWidth: 0.3,
    borderBottomWidth: 0.3,
    borderColor: C.border,
    minHeight: 13,
  },
  tRowAlt: {
    flexDirection: "row",
    borderLeftWidth: 0.3,
    borderRightWidth: 0.3,
    borderTopWidth: 0.3,
    borderBottomWidth: 0.3,
    borderColor: C.border,
    minHeight: 13,
    backgroundColor: C.rowAlt,
  },
  tRowFoot: {
    flexDirection: "row",
    borderLeftWidth: 0.5,
    borderRightWidth: 0.5,
    borderTopWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: C.accent,
    backgroundColor: "#eef2ff",
    minHeight: 13,
  },
  tCellHead: {
    fontSize: 5.5,
    color: C.white,
    fontFamily: "Helvetica-Bold",
    paddingHorizontal: 2,
    paddingVertical: 2,
    textAlign: "left",
    flexWrap: "wrap",
    borderRightWidth: 0.3,
    borderRightColor: C.border,
  },
  tCell: {
    fontSize: 5.5,
    paddingHorizontal: 2,
    paddingVertical: 2,
    flexWrap: "wrap",
    textAlign: "left",
    borderRightWidth: 0.3,
    borderRightColor: C.border,
  },
  tCellRight: {
    fontSize: 5.5,
    paddingHorizontal: 2,
    paddingVertical: 2,
    textAlign: "left",
    fontFamily: "Helvetica-Bold",
    borderRightWidth: 0.3,
    borderRightColor: C.border,
  },
  noData: {
    fontSize: 6,
    color: C.light,
    fontStyle: "italic",
    textAlign: "center",
    paddingVertical: 4,
    borderWidth: 0.3,
    borderColor: C.border,
  },

  // ── Signature section ─────────────────────────────────────────
  signWrap: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 30,
    paddingTop: 12,
    borderTopWidth: 0.5,
    borderTopColor: C.border,
  },
  signCol: { width: "28%", alignItems: "center" },
  signRole: {
    fontSize: 6,
    color: C.mid,
    marginBottom: 28,
    textAlign: "center",
  },
  signLine: {
    width: "100%",
    borderBottomWidth: 0.5,
    borderBottomColor: C.dark,
    marginBottom: 3,
  },
  signName: { fontSize: 6, color: C.mid, textAlign: "center" },

  // ── Footer (page number) ───────────────────────────────────────
  pageFooter: {
    position: "absolute",
    bottom: 16,
    left: 28,
    right: 28,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 0.3,
    borderTopColor: C.border,
    paddingTop: 3,
  },
  footText: { fontSize: 5, color: C.light },
});

// ─── Sub-components ──────────────────────────────────────────────────────────

export const SHRow = ({ label, value }) => (
  <View style={s.subHeaderRow}>
    <Text style={s.subHeaderLabel}>{label}</Text>
    <Text style={s.subHeaderColon}>:</Text>
    <Text style={s.subHeaderVal}>{value || "-"}</Text>
  </View>
);

/** Summary row component for totals at the bottom of tables */
const SummaryRow = ({ colCount, widths, summaryData }) => {
  if (!summaryData || summaryData.length === 0) return null;
  
  // Find the first non-empty summary data index to place "Total" label
  let firstDataIndex = -1;
  for (let i = 0; i < summaryData.length; i++) {
    if (summaryData[i] && summaryData[i] !== "") {
      firstDataIndex = i;
      break;
    }
  }
  
  return (
    <View style={s.tRowFoot}>
      {Array.from({ length: colCount }).map((_, i) => {
        const isLastCell = i === colCount - 1;
        const isFirstDataCell = i === firstDataIndex;
        
        return (
          <Text
            key={i}
            style={[
              s.tCell,
              { width: widths[i], fontFamily: "Helvetica-Bold" },
              isLastCell && { borderRightWidth: 0 },
            ]}
          >
            {isFirstDataCell ? `Total ${summaryData[i] || ""}` : (summaryData[i] || "")}
          </Text>
        );
      })}
    </View>
  );
};

/** Helper to create table component with summary row */
const createTableWithSummary = (config) => {
  const { tableName, headers, widths, fields, summaryCalculator } = config;
  
  const TableComponent = ({ rows = [], title }) => {
    const summaryData = summaryCalculator ? summaryCalculator(rows) : null;
    
    if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
    
    return (
      <View wrap={false}>
        <Text style={s.subTitle}>{title}</Text>
        <View style={s.table}>
          <View style={s.tRowHead}>
            {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
          </View>
          {rows.map((r, ri) => (
            <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
              {fields.map((f, fi) => (
                <Text 
                  key={fi} 
                  style={[
                    s.tCell, 
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
            </View>
          ))}
          {summaryData && (
            <SummaryRow
              colCount={headers.length}
              widths={widths}
              summaryData={summaryData}
            />
          )}
        </View>
      </View>
    );
  };
  
  TableComponent.displayName = tableName;
  return TableComponent;
};

/** Generic activity table driven by objectDrawer definition */
const ActivityTable = ({ drawerKey, rows = [], subTitle, colWidths }) => {
  const drawer = objectDrawer[drawerKey];
  if (!drawer) return null;

  const headers = flattenHeaders(drawer.header_upper, drawer.header);
  const fields = getBodyFields(drawer.body);
  const widths = colWidths || deriveWidths(headers);

  if (!rows.length) {
    return (
      <View>
        {subTitle && <Text style={s.subTitle}>{subTitle}</Text>}
        <Text style={s.noData}>Tidak ada data</Text>
      </View>
    );
  }

  return (
    <View wrap={false}>
      {subTitle && <Text style={s.subTitle}>{subTitle}</Text>}
      <View style={s.table}>
        {/* Header */}
        <View style={s.tRowHead} fixed>
          {headers.map((h, i) => (
            <Text key={i} style={[s.tCellHead, { width: widths[i] || "10%" }]}>
              {h}
            </Text>
          ))}
        </View>
        {/* Data rows */}
        {rows.map((row, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
              <Text key={fi} style={[s.tCell, { width: widths[fi] || "10%" }]}>
                {renderCell(f.type, f.key, row, ri)}
              </Text>
            ))}
          </View>
        ))}
      </View>
    </View>
  );
};

// ─── Specific table definitions (matching Go template columns) ─────────────

/** PORT_SERVICES: Stevedoring / Port_Service_Common */
const StevedoringTable = ({ rows = [], title }) => {
  const headers = ["Material Descriptions", "Type of Packaging", "Operation Type", "Quantity", "UoM", "Remarks"];
  const widths =  ["25%", "15%", "15%", "15%", "10%", "20%"];
  const fields =  [
    { key: "form1", type: "text" },
    { key: "form2", type: "text" },
    { key: "form3", type: "text" },
    { key: "form4", type: "text" },
    { key: "form5", type: "oum" },
    { key: "form8", type: "text" },
  ];
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
      </View>
    </View>
  );
};

/** PORT_SERVICES: Vessel Service / Vessel Berthing */
const VesselServiceTable = ({ rows = [], title }) => {
  const headers = ["Jetty", "Vessel Name", "Arrival", "Departure", "Activity", "Total Hours", "Remarks"];
  const widths =  ["13%", "20%", "14%", "14%", "12%", "12%", "15%"];
  const fields = [
    { key: "form1", type: "jetty" },
    { key: "form2", type: "text" },
    { key: "date1", type: "dateTime" },
    { key: "date2", type: "dateTime" },
    { key: "form5", type: "text" },
    { key: "__vesselTotalHours", type: "computed" },
    { key: "form3", type: "text" },
  ];
  
  // Calculate summary totals
  const totalHours = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__vesselTotalHours", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);
  
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
        {/* Summary Row */}
        <SummaryRow
          colCount={headers.length}
          widths={widths}
          summaryData={["", "", "", "", "", String(totalHours), ""]}
        />
      </View>
    </View>
  );
};

/** PORT_SERVICES: Liquid / Bulk Transfer */
const LiquidTable = ({ rows = [], title }) => {
  const headers = ["Material Descriptions", "Berthing Date", "Vessel / Facility", "Flow Start", "Flow End", "Total", "UoM", "Remarks"];
  const widths =  ["21%", "13%", "17%", "10%", "10%", "8%", "8%", "13%"];
  const fields = [
    { key: "form1", type: "text" },
    { key: "date1", type: "date" },
    { key: "form2", type: "text" },
    { key: "form3", type: "text" },
    { key: "form4", type: "text" },
    { key: "__liquidTotal", type: "computed" },
    { key: "form5", type: "oum" },
    { key: "form6", type: "text" },
  ];
  
  // Calculate summary totals
  const totalLiquid = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__liquidTotal", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);
  
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
        {/* Summary Row */}
        <SummaryRow
          colCount={headers.length}
          widths={widths}
          summaryData={["", "", "", "", "", String(totalLiquid), "", ""]}
        />
      </View>
    </View>
  );
};

/** PORT_SERVICES: Cargodoring */
const CargodoringTable = ({ rows = [], title }) => {
  const headers = ["Material Descriptions", "Type of Packaging", "Loading Qty", "UoM", "Destination", "Remarks"];
  const widths =  ["27%", "17%", "14%", "8%", "18%", "16%"];
  const fields = [
    { key: "form1", type: "text" },
    { key: "form2", type: "text" },
    { key: "form4", type: "text" },
    { key: "form5", type: "oum" },
    { key: "form6", type: "text" },
    { key: "form7", type: "text" },
  ];
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
      </View>
    </View>
  );
};

/** PORT_SERVICES: Receiving / Delivery variants (Port_Service_External) */
const ExternalPortTable = ({ rows = [], title }) => {
  const headers = ["Material Descriptions", "Type of Package", "UoM", "Quantity", "UoM", "Remarks"];
  const widths =  ["30%", "20%", "10%", "14%", "10%", "16%"];
  const fields = [
    { key: "form1", type: "text" },
    { key: "form2", type: "text" },
    { key: "form3", type: "oum" },
    { key: "form4", type: "text" },
    { key: "form5", type: "oum" },
    { key: "form6", type: "text" },
  ];
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
      </View>
    </View>
  );
};

/** RENTAL */
const RentalTable = ({ rows = [], title }) => {
  const headers = ["Descriptions", "Detail Unit", "Unit No", "Qty", "UoM", "Date/Time Start", "Date/Time End", "Total", "Remarks"];
  const widths =  ["16%", "13%", "11%", "6%", "6%", "13%", "13%", "10%", "12%"];
  const fields = [
    { key: "form1", type: "text" },
    { key: "form2", type: "text" },
    { key: "form3", type: "text" },
    { key: "form6", type: "text" },
    { key: "form4", type: "oum" },
    { key: "date1", type: "dateTime" },
    { key: "date2", type: "dateTime" },
    { key: "__rentalTotal", type: "computed" },
    { key: "form5", type: "text" },
  ];
  
  // Calculate summary totals
  const totalQty = rows.reduce((sum, r) => sum + (parseFloat(r.form6) || 0), 0);
  const totalRental = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__rentalTotal", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);
  
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
        {/* Summary Row */}
        <SummaryRow
          colCount={headers.length}
          widths={widths}
          summaryData={["", "", "", String(totalQty), "", "", "", String(totalRental), ""]}
        />
      </View>
    </View>
  );
};

/** MANPOWER */
const ManpowerTable = ({ rows = [], title }) => {
  const headers = ["Descriptions", "Working Date Start", "Working Date End", "Time Start", "Time End", "Total Hour", "Total Manpower", "Remarks"];
  const widths =  ["22%", "11%", "11%", "9%", "9%", "9%", "12%", "17%"];
  const fields = [
    { key: "form1", type: "text" },
    { key: "date1", type: "date" },
    { key: "date2", type: "date" },
    { key: "date1", type: "time" },
    { key: "date2", type: "time" },
    { key: "__totalHour", type: "computed" },
    { key: "form2", type: "text" },
    { key: "form3", type: "text" },
  ];
  
  // Calculate summary totals
  const totalHours = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__totalHour", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);
  const totalManpower = rows.reduce((sum, r) => sum + (parseFloat(r.form2) || 0), 0);
  
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
        {/* Summary Row */}
        <SummaryRow
          colCount={headers.length}
          widths={widths}
          summaryData={["", "", "", "", "", String(totalHours), String(totalManpower), ""]}
        />
      </View>
    </View>
  );
};

/** AREA_RENTAL */
const AreaRentalTable = ({ rows = [], title }) => {
  const headers = ["Description", "Qty", "UoM", "Start Date", "End Date", "Total Days", "L", "W", "H", "Total Dim.", "Dim UoM", "Total Rental", "Remarks"];
  const widths =  ["13%", "5%", "5%", "9%", "9%", "6%", "4%", "4%", "4%", "8%", "5%", "9%", "9%"];
  const fields = [
    { key: "form1", type: "text" },
    { key: "form2", type: "text" },
    { key: "form3", type: "oum" },
    { key: "date1", type: "date" },
    { key: "date2", type: "date" },
    { key: "__totalDays", type: "computed" },
    { key: "form4", type: "text" },
    { key: "form5", type: "text" },
    { key: "form6", type: "text" },
    { key: "__totalDim", type: "computed" },
    { key: "form7", type: "oum" },
    { key: "__tAreaRental", type: "computed" },
    { key: "form8", type: "text" },
  ];
  
  // Calculate summary totals
  const totalQty = rows.reduce((sum, r) => sum + (parseFloat(r.form2) || 0), 0);
  const totalDays = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__totalDays", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);
  const totalDim = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__totalDim", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);
  const totalRental = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__tAreaRental", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);
  
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
        {/* Summary Row */}
        <SummaryRow
          colCount={headers.length}
          widths={widths}
          summaryData={["", String(totalQty), "", "", "", String(totalDays), "", "", "", String(totalDim), "", String(totalRental), ""]}
        />
      </View>
    </View>
  );
};

/** ROOM */
const RoomTable = ({ rows = [], title }) => {
  const headers = ["Room Number", "Person", "Date Start", "Date End", "Total Day", "Total", "Name", "Company", "Sponsor Company", "Remarks"];
  const widths =  ["10%", "7%", "10%", "10%", "7%", "7%", "12%", "12%", "12%", "13%"];
  const fields = [
    { key: "form1", type: "text" },
    { key: "form6", type: "text" },
    { key: "date1", type: "date" },
    { key: "date2", type: "date" },
    { key: "form7", type: "text" },
    { key: "form8", type: "text" },
    { key: "form2", type: "text" },
    { key: "form3", type: "text" },
    { key: "form4", type: "text" },
    { key: "form5", type: "text" },
  ];
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
      </View>
    </View>
  );
};

/** MEALS */
const MealsTable = ({ rows = [], title }) => {
  const headers = ["Descriptions", "Qty", "UoM", "Date", "Total Meal", "Name", "Company", "Sponsor Company", "Remarks"];
  const widths =  ["20%", "7%", "7%", "10%", "9%", "13%", "12%", "12%", "10%"];
  const fields = [
    { key: "form1", type: "text" },
    { key: "form6", type: "text" },
    { key: "form7", type: "oum" },
    { key: "date1", type: "date" },
    { key: "__tMeal", type: "computed" },
    { key: "form2", type: "text" },
    { key: "form3", type: "text" },
    { key: "form4", type: "text" },
    { key: "form5", type: "text" },
  ];
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;

  // Calculate summary totals
  const totalMeal = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__tMeal", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);

  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
        {/* Summary Row */}
        <SummaryRow
          colCount={headers.length}
          widths={widths}
          summaryData={["", "", "", "", String(totalMeal), "", "", "", ""]}
        />
      </View>
    </View>
  );
};

/** LAUNDRY */
const LaundryTable = ({ rows = [], title }) => {
  const headers = ["Type Of Cloth", "Weight", "UoM", "Tanggal", "Name", "Company", "Sponsor Company", "Remarks"];
  const widths =  ["18%", "10%", "8%", "12%", "15%", "13%", "13%", "11%"];
  const fields = [
    { key: "form1", type: "text" },
    { key: "form6", type: "text" },
    { key: "form7", type: "oum" },
    { key: "date1", type: "date" },
    { key: "form2", type: "text" },
    { key: "form3", type: "text" },
    { key: "form4", type: "text" },
    { key: "form5", type: "text" },
  ];
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
      </View>
    </View>
  );
};

/** WOODEN */
const WoodenTable = ({ rows = [], title }) => {
  const headers = ["Descriptions", "Location Used", "Start Date", "End Date", "Total", "Quantity", "UoM", "Remarks"];
  const widths =  ["20%", "16%", "11%", "11%", "9%", "9%", "8%", "16%"];
  const fields = [
    { key: "form1", type: "text" },
    { key: "form2", type: "text" },
    { key: "date1", type: "date" },
    { key: "date2", type: "date" },
    { key: "__woodenTotal", type: "computed" },
    { key: "form3", type: "text" },
    { key: "form4", type: "text" },
    { key: "form5", type: "oum" },
  ];
  
  // Calculate summary totals
  const totalDays = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__woodenTotal", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);
  const totalQty = rows.reduce((sum, r) => sum + (parseFloat(r.form3) || 0), 0);
  
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
        {/* Summary Row */}
        <SummaryRow
          colCount={headers.length}
          widths={widths}
          summaryData={["", "", "", "", String(totalDays), String(totalQty), "", ""]}
        />
      </View>
    </View>
  );
};

/** Drill */
const DrillTable = ({ rows = [], title }) => {
  const headers = ["Description", "Date Start", "Date End", "Total Time", "User", "Sponsor", "Flow Start", "Flow End", "Total", "UoM", "Remarks"];
  const widths =  ["14%", "11%", "11%", "7%", "9%", "9%", "7%", "7%", "6%", "6%", "13%"];
  const fields = [
    { key: "form1", type: "text" },
    { key: "date1", type: "dateTime" },
    { key: "date2", type: "dateTime" },
    { key: "__drillTotalTime", type: "computed" },
    { key: "form2", type: "text" },
    { key: "form3", type: "text" },
    { key: "form4", type: "text" },
    { key: "form5", type: "text" },
    { key: "__drillTotal", type: "computed" },
    { key: "form6", type: "oum" },
    { key: "form7", type: "text" },
  ];
  
  // Calculate summary totals
  const totalTime = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__drillTotalTime", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);
  const totalDrill = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__drillTotal", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);
  
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
        {/* Summary Row */}
        <SummaryRow
          colCount={headers.length}
          widths={widths}
          summaryData={["", "", "", String(totalTime), "", "", "", "", String(totalDrill), "", ""]}
        />
      </View>
    </View>
  );
};

/** Fuel */
const FuelTable = ({ rows = [], title }) => {
  const headers = ["Date", "Fuel Description", "User", "Sponsor", "Flow Start", "Flow End", "Total", "UoM", "Remarks"];
  const widths =  ["11%", "16%", "13%", "13%", "10%", "10%", "7%", "7%", "13%"];
  const fields = [
    { key: "date1", type: "date" },
    { key: "form1", type: "text" },
    { key: "form7", type: "text" },
    { key: "form2", type: "text" },
    { key: "form3", type: "text" },
    { key: "form4", type: "text" },
    { key: "__fuelTotal", type: "computed" },
    { key: "form5", type: "oum" },
    { key: "form6", type: "text" },
  ];
  
  // Calculate summary totals
  const totalFuel = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__fuelTotal", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);
  
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
        {/* Summary Row */}
        <SummaryRow
          colCount={headers.length}
          widths={widths}
          summaryData={["", "", "", "", "", "", String(totalFuel), "", ""]}
        />
      </View>
    </View>
  );
};

/** Vehicle */
const VehicleTable = ({ rows = [], title }) => {
  const headers = ["Detail Unit", "Unit Number", "Qty", "UoM", "Date Start", "Date End", "Total", "Remarks"];
  const widths =  ["18%", "15%", "8%", "8%", "13%", "13%", "10%", "15%"];
  const fields = [
    { key: "form1", type: "text" },
    { key: "form2", type: "text" },
    { key: "form3", type: "text" },
    { key: "form4", type: "oum" },
    { key: "date1", type: "date" },
    { key: "date2", type: "date" },
    { key: "__vehicleTotalDays", type: "computed" },
    { key: "form5", type: "text" },
  ];
  
  // Calculate summary totals
  const totalQty = rows.reduce((sum, r) => sum + (parseFloat(r.form3) || 0), 0);
  const totalDays = rows.reduce((sum, r) => {
    const val = renderCell("computed", "__vehicleTotalDays", r, 0);
    return sum + (parseFloat(val) || 0);
  }, 0);
  
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
        {/* Summary Row */}
        <SummaryRow
          colCount={headers.length}
          widths={widths}
          summaryData={["", "", String(totalQty), "", "", "", String(totalDays), ""]}
        />
      </View>
    </View>
  );
};

/** Clearance Doc */
const ClearanceDocTable = ({ rows = [], title }) => {
  const headers = ["Document Number", "Document Name", "Quantity", "Remarks"];
  const widths =  ["25%", "35%", "15%", "25%"];
  const fields = [
    { key: "form2", type: "text" },
    { key: "form3", type: "text" },
    { key: "form4", type: "text" },
    { key: "form5", type: "text" },
  ];
  if (!rows.length) return <View><Text style={s.subTitle}>{title}</Text><Text style={s.noData}>Tidak ada data</Text></View>;
  return (
    <View wrap={false}>
      <Text style={s.subTitle}>{title}</Text>
      <View style={s.table}>
        <View style={s.tRowHead}>
          {headers.map((h, i) => (
              <Text
                key={i}
                style={[
                  s.tCellHead,
                  { width: widths[i] },
                  i === headers.length - 1 && { borderRightWidth: 0 },
                ]}
              >
                {h}
              </Text>
            ))}
        </View>
        {rows.map((r, ri) => (
          <View key={ri} style={ri % 2 === 0 ? s.tRow : s.tRowAlt}>
            {fields.map((f, fi) => (
                <Text
                  key={fi}
                  style={[
                    s.tCell,
                    { width: widths[fi] },
                    fi === fields.length - 1 && { borderRightWidth: 0 },
                  ]}
                >
                  {renderCell(f.type, f.key, r, ri)}
                </Text>
              ))}
          </View>
        ))}
      </View>
    </View>
  );
};

// ─── PORT_SERVICES router ─────────────────────────────────────────────────────

const PortServiceSection = ({ sub }) => {
  const name = sub.Name || "";
  const rows = sub.Detail || [];
  const title = (sub.Surname || sub.Name || "").replace(/_/g, " ");

  if (name === "Stevedoring")
    return <StevedoringTable rows={rows} title={title} />;
  if (name === "Liquid" || name === "Bulk_Transfer")
    return <LiquidTable rows={rows} title={title} />;
  if (name === "Cargodoring")
    return <CargodoringTable rows={rows} title={title} />;
  if (name === "Vessel_Service" || name === "Vessel_Berthing")
    return <VesselServiceTable rows={rows} title={title} />;
  if (["Receiving_Using_Forklift", "Receiving_Using_Crane",
       "Delivery_Using_Forklift", "Delivery_Using_Crane",
       "Delivery", "Receiving", "Stacking_atau_Restacking"].includes(name))
    return <ExternalPortTable rows={rows} title={title} />;

  // fallback to generic objectDrawer table
  const key = portServiceKey(name);
  return <ActivityTable drawerKey={key} rows={rows} subTitle={title} />;
};

// ─── GENERAL_SERVICE router ──────────────────────────────────────────────────

const GeneralServiceSection = ({ sub }) => {
  const name = sub.Name || "";
  const rows = sub.Detail || [];
  const title = (sub.Surname || sub.Name || "").replace(/_/g, " ");
  const normalizedName = name.toUpperCase().trim();

  if (normalizedName === "ROOM" || normalizedName.includes("ROOM"))           return <RoomTable rows={rows} title={title} />;
  if (normalizedName === "MEALS" || normalizedName.includes("MEALS") || normalizedName.includes("CANTEEN"))          return <MealsTable rows={rows} title={title} />;
  if (normalizedName === "LAUNDRY" || normalizedName.includes("LAUNDRY"))        return <LaundryTable rows={rows} title={title} />;
  if (normalizedName === "WOODEN" || normalizedName.includes("WOODEN"))         return <WoodenTable rows={rows} title={title} />;
  if (normalizedName === "DRILL" || normalizedName.includes("DRILL"))          return <DrillTable rows={rows} title={title} />;
  if (normalizedName === "FUEL" || normalizedName.includes("FUEL"))           return <FuelTable rows={rows} title={title} />;
  if (normalizedName === "VEHICLE" || normalizedName.includes("VEHICLE"))        return <VehicleTable rows={rows} title={title} />;
  if (normalizedName === "CLEARANCE DOC" || normalizedName.includes("CLEARANCE"))  return <ClearanceDocTable rows={rows} title={title} />;

  // Generic fallback using objectDrawer
  const key = generalServiceKey(sub);
  return <ActivityTable drawerKey={key} rows={rows} subTitle={title} />;
};

// ─── Top-level activity router ────────────────────────────────────────────────

const TopActivitySection = ({ act }) => {
  const name = act.Name || "";
  const rows = act.Detail || [];
  const title = name.replace(/_/g, " ");

  if (name === "RENTAL")    return <RentalTable rows={rows} title={title} />;
  if (name === "MANPOWER")  return <ManpowerTable rows={rows} title={title} />;
  if (name === "AREA_RENTAL") return <AreaRentalTable rows={rows} title={title} />;

  // Generic fallback
  const drawer = objectDrawer[name];
  if (drawer) return <ActivityTable drawerKey={name} rows={rows} subTitle={title} />;

  return (
    <View>
      <Text style={s.subTitle}>{title}</Text>
      <Text style={s.noData}>{rows.length} baris data</Text>
    </View>
  );
};

// ─── Main PDF Document ────────────────────────────────────────────────────────

const JRDocument = ({ data }) => {
  if (!data) return null;

  // Sort PORT_SERVICES DetailActivity by canonical order
  let activities = data.Activity || data.SubActivity || [];
  try {
    if (activities.length > 0) {
      const clone = JSON.parse(JSON.stringify(activities));
      activities = SortingPORT_Services(clone) || clone;
    }
  } catch { /* ignore */ }

  const equipment  = data.Equipment  || [];
  const labour     = data.Labour     || [];
  const truckEntry = data.TruckEntry || [];

  return (
    <Document>
      <Page size="A4" style={s.page} wrap>

        {/* ── PAGE FOOTER (fixed, every page) ── */}
        <View style={s.pageFooter} fixed>
          <Text style={s.footText}>
            PT. RANJI KARYA SAKTI | Pendingin, Sanga Sanga, Kutai Kartanegara, East Kalimantan 75267
          </Text>
          <Text
            style={s.footText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>

        {/* ── HEADER ── */}
        <View style={s.headerWrap}>

          {/* Left: company info */}
          <View style={s.headerLeft}>
            <Text style={s.companyName}>PT. RANJI KARYA SAKTI</Text>
            <Text style={s.companyAddr}>
              Pendingin, Sanga Sanga, Kutai Kartanegara{"\n"}
              East Kalimantan 75267{"\n"}
              Telp: (021) 22909819 | info@ranjikaryasakti.co.id
            </Text>
          </View>

          {/* Center: title */}
          <View style={s.headerCenter}>
            <View style={s.titleBox}>
              <Text style={s.titleText}>JOB REQUEST</Text>
            </View>
          </View>

          {/* Right: document info box */}
          <View style={s.headerRight}>
            <View style={s.docInfoTable}>
              <View style={s.docInfoRow}>
                <Text style={s.docInfoLabel}>Document No.</Text>
                <Text style={s.docInfoVal}>RKS-OPS-FO-001</Text>
              </View>
              <View style={s.docInfoRow}>
                <Text style={s.docInfoLabel}>Issued Date</Text>
                <Text style={s.docInfoVal}>12-08-2018</Text>
              </View>
              <View style={s.docInfoRow}>
                <Text style={s.docInfoLabel}>Revision</Text>
                <Text style={s.docInfoVal}>0</Text>
              </View>
              <View style={s.docInfoRowLast}>
                <Text style={s.docInfoLabel}>Page</Text>
                <Text
                  style={s.docInfoVal}
                  render={({ pageNumber, totalPages }) =>
                    `${pageNumber} of ${totalPages}`
                  }
                />
              </View>
            </View>
          </View>

        </View>

        {/* ── SUB-HEADER: contract / JR info + activity checklist ── */}
        <View style={s.subHeaderBox}>
          <SHRow label="Contract No" value={data.QuatationNo} />
          <SHRow label="Nomor JR"    value={data.JRID} />
          <SHRow label="SPK No"      value={data.SPKNo} />
          <SHRow label="Priority"    value={data.Priority} />
          <SHRow label="Request Unit" value={data.RequestUnit} />

          <Text style={s.subHeaderGreeting}>
            Kepada / To: {"PT. Ranji Karya Sakti (Kutai Logistic Base)"}
            {data.NamaKlien
              ? `\nBersama ini kami mengajukan Job Request kepada Bapak/Ibu ${data.NamaKlien}`
              : ""}
          </Text>

          {/* Activity checklist */}
          {activities.length > 0 && (
            <View style={s.checklistWrap}>
              {activities.map((act, i) => (
                <View key={i} style={s.checklistItem}>
                  <Text style={act.Checked ? s.checkBoxChecked : s.checkBox}>
                    {act.Checked ? "T" : " "}
                  </Text>
                  <Text style={s.checkLabel}>
                    {(act.Name || "").replace(/_/g, " ")}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* ── DETAIL ACTIVITY ── */}
        {activities.length > 0 && (
          <View>
            <Text style={s.secTitle}>DETAIL ACTIVITY</Text>

            {activities.map((act, ai) => {
              const name = act.Name || "";

              // PORT_SERVICES: has DetailActivity with sub-activities
              if (name === "PORT_SERVICES" && act.DetailActivity) {
                return (
                  <View key={ai}>
                    <Text style={[s.subTitle, { backgroundColor: "#374151" }]}>
                      PORT SERVICES
                    </Text>
                    {act.DetailActivity.map((sub, si) => (
                      <PortServiceSection key={si} sub={sub} />
                    ))}
                  </View>
                );
              }

              // GENERAL_SERVICE: has DetailActivity with sub-activities
              if (name === "GENERAL_SERVICE" && act.DetailActivity) {
                return (
                  <View key={ai}>
                    <Text style={[s.subTitle, { backgroundColor: "#374151" }]}>
                      GENERAL SERVICE
                    </Text>
                    {act.DetailActivity.map((sub, si) => (
                      <GeneralServiceSection key={si} sub={sub} />
                    ))}
                  </View>
                );
              }

              // Top-level activities (RENTAL, MANPOWER, AREA_RENTAL, etc.)
              return <TopActivitySection key={ai} act={act} />;
            })}
          </View>
        )}

        {/* ── EQUIPMENT ── */}
        {equipment.length > 0 && (
          <View>
            <Text style={s.secTitle}>EQUIPMENT</Text>
            <View style={s.table}>
              <View style={s.tRowHead}>
                {["Unit", "Unit Number", "Start Time", "Finish Time", "Total Time", "Hour Meter", "Total Meter", "Remarks"].map((h, i) => {
                  const w = ["18%", "15%", "14%", "14%", "10%", "9%", "9%", "11%"][i];
                  return <Text key={i} style={[s.tCellHead, { width: w }]}>{h}</Text>;
                })}
              </View>
              {equipment.map((eq, i) => (
                <View key={i} style={i % 2 === 0 ? s.tRow : s.tRowAlt}>
                  {[
                    { w: "18%", v: eq.Unit       || "-" },
                    { w: "15%", v: eq.UnitNumber  || "-" },
                    { w: "14%", v: fmtDateTime(eq.StartTime) },
                    { w: "14%", v: fmtDateTime(eq.FinishTime) },
                    { w: "10%", v: eq.TotalHour  || "-" },
                    { w: "9%",  v: eq.HourMeter  || "-" },
                    { w: "9%",  v: eq.TotalMeter || eq.HourMeter || "-" },
                    { w: "11%", v: eq.Remarks    || "-" },
                  ].map((c, ci) => (
                    <Text key={ci} style={[s.tCell, { width: c.w }]}>{c.v}</Text>
                  ))}
                </View>
              ))}
            </View>
          </View>
        )}

        {/* ── LABOUR ── */}
        {labour.length > 0 && (
          <View>
            <Text style={s.secTitle}>LABOUR</Text>
            <View style={s.table}>
              <View style={s.tRowHead}>
                {["Labour Descriptions", "Start Time", "Finish Time", "Total", "Total Man Power", "Remarks"].map((h, i) => {
                  const w = ["28%", "16%", "16%", "12%", "14%", "14%"][i];
                  return <Text key={i} style={[s.tCellHead, { width: w }]}>{h}</Text>;
                })}
              </View>
              {labour.map((lb, i) => (
                <View key={i} style={i % 2 === 0 ? s.tRow : s.tRowAlt}>
                  {[
                    { w: "28%", v: lb.LabourDesc   || lb.Descriptions || "-" },
                    { w: "16%", v: fmtDateTime(lb.StartTime) },
                    { w: "16%", v: fmtDateTime(lb.FinishTime) },
                    { w: "12%", v: lb.TotalHour    || "-" },
                    { w: "14%", v: lb.TotalManPower || lb.TotalManpower || "-" },
                    { w: "14%", v: lb.Remarks       || "-" },
                  ].map((c, ci) => (
                    <Text key={ci} style={[s.tCell, { width: c.w }]}>{c.v}</Text>
                  ))}
                </View>
              ))}
            </View>
          </View>
        )}

        {/* ── TRUCKS ENTRY ── */}
        {truckEntry.length > 0 && (
          <View>
            <Text style={s.secTitle}>TRUCKS ENTRY</Text>
            <View style={s.table}>
              <View style={s.tRowHead}>
                {["Material Desc", "Transporter", "Unit Number", "Start", "Finish", "Total", "Remarks"].map((h, i) => {
                  const w = ["22%", "18%", "14%", "14%", "14%", "9%", "9%"][i];
                  return <Text key={i} style={[s.tCellHead, { width: w }]}>{h}</Text>;
                })}
              </View>
              {truckEntry.map((tr, i) => (
                <View key={i} style={i % 2 === 0 ? s.tRow : s.tRowAlt}>
                  {[
                    { w: "22%", v: tr.MaterialDesc || "-" },
                    { w: "18%", v: tr.Transporter  || "-" },
                    { w: "14%", v: tr.UnitNumber   || "-" },
                    { w: "14%", v: fmtDateTime(tr.EntryDate  || tr.StartTime) },
                    { w: "14%", v: fmtDateTime(tr.ExitDate   || tr.FinishTime) },
                    { w: "9%",  v: tr.TotalHour    || "-" },
                    { w: "9%",  v: tr.Remarks      || "-" },
                  ].map((c, ci) => (
                    <Text key={ci} style={[s.tCell, { width: c.w }]}>{c.v}</Text>
                  ))}
                </View>
              ))}
            </View>
          </View>
        )}

        {/* ── SIGNATURE ── */}
        <View style={s.signWrap}>
          {["Dibuat Oleh\n(Operasional)", "Disetujui Oleh\n(Manager)", "Diketahui Oleh\n(Klien)"].map((label, i) => (
            <View key={i} style={s.signCol}>
              <Text style={s.signRole}>{label}</Text>
              <View style={s.signLine} />
              <Text style={s.signName}>(                    )</Text>
            </View>
          ))}
        </View>

      </Page>
    </Document>
  );
};

// ─── Page Component ───────────────────────────────────────────────────────────

const CetakJR = () => {
  const location = useLocation();
  const [oumData, setOumData] = React.useState({});

  // Fetch OUM data for lookup
  const fetchOumData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const response = await axios.post(
        process.env.REACT_APP_API_URL + "MasterOum/getMin",
        new FormData(),
        {
          headers: {
            Authorization: "Bearer " + user?.Token,
            Accept: "*/*",
          },
        },
      );
      if (response.status === 200 && response.data?.data) {
        // Create a map: { OumID: NamaOum }
        const oumMap = {};
        response.data.data.forEach(item => {
          oumMap[item.OumID] = item.NamaOum;
        });
        setOumData(oumMap);
        // Also populate the global cache for fmtOum helper
        oumLookupCache = oumMap;
      }
    } catch (error) {
      console.error("Failed to fetch OUM data:", error);
    }
  };

  // Fetch Jetty data for lookup
  const fetchJettyData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const formData = new FormData();
      formData.append("search", "");
      const response = await axios.post(
        process.env.REACT_APP_API_URL + "getMinMasterJetty",
        formData,
        {
          headers: {
            Authorization: "Bearer " + user?.Token,
            Accept: "*/*",
          },
        },
      );
      if (response.status === 200 && response.data?.data) {
        // Create a map: { JettyID: NamaJetty }
        const jettyMap = {};
        response.data.data.forEach(item => {
          jettyMap[item.JettyID] = item.NamaJetty;
        });
        // Populate the global cache for fmtJetty helper
        jettyLookupCache = jettyMap;
        console.log("Jetty data loaded:", jettyMap);
      }
    } catch (error) {
      console.error("Failed to fetch Jetty data:", error);
    }
  };

  useEffect(() => {
    fetchOumData();
    fetchJettyData();
  }, []);

  const mutationGetJR = useMutation(async (jrid) => {
    const user = JSON.parse(localStorage.getItem("user"));
    const formData = new FormData();
    formData.append("JRID", jrid);
    const response = await axios.post(
      process.env.REACT_APP_API_URL + "getJobRequestDetailUserActivity",
      formData,
      {
        headers: {
          Authorization: "Bearer " + user?.Token,
          Accept: "*/*",
        },
      },
    );
    return response.data;
  });

  useEffect(() => {
    if (location.state?.JRID) {
      mutationGetJR.mutate(location.state.JRID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return (
    <div className="md:ml-64">
      <div
        style={{ background: Colors.primary }}
        className="pt-14 pb-16 px-3 md:px-8 h-auto"
      >
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1" />
        </div>
      </div>

      <div className="px-3 md:px-8 h-screen -mt-32">
        {mutationGetJR.isLoading && (
          <div className="flex justify-center items-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto" />
              <p className="mt-4 text-gray-600">Memuat dokumen...</p>
            </div>
          </div>
        )}

        {mutationGetJR.error && (
          <div className="flex justify-center items-center h-full">
            <div className="text-center">
              <p className="text-red-500 text-xl font-bold">Gagal memuat Job Request</p>
              <p className="text-gray-500 mt-2 text-sm">{mutationGetJR.error.message}</p>
            </div>
          </div>
        )}

        {mutationGetJR.data && (
          <div className="h-screen w-full overflow-hidden">
            <PDFViewer
              className="h-screen w-full"
              style={{ width: "100%", height: "100%", minWidth: "100%", minHeight: "100%" }}
            >
              <JRDocument data={mutationGetJR.data.data} oumData={oumData} />
            </PDFViewer>
          </div>
        )}
      </div>
    </div>
  );
};

export default CetakJR;
